import { useParams } from '@solidjs/router';
import { createEffect, createResource, createSignal, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Screen } from '../../components/layout/screen/screen';
import { ProductCategory as ProductCategoryType } from '../../components/product-categories/product-categories.d';
import { ProductCategory } from '../../components/product-categories/product-category';
import { getProductCategoryQuery } from '../../graphql/queries/product-categories';
import { getOriginProductsQuery } from '../../graphql/queries/products';
import { multiSearchQuery } from '../../graphql/queries/registration-number-check';

export type MultiSearchResultType = {
    name: string;
    id: string;
    regNumber: string;
    productName: string;
    productSlug: string;
    productRegistrationNumber: string;
    approvalCeasedDate: string | null;
    usageForbiddenDate: string | null;
};

export const ProductCategoryContainer = () => {
    const { categorySlug } = useParams();
    const { createCachedResource, graphQlClient } = useContext(AppContext);
    const [categoryData, setCategoryData] = createSignal<ProductCategoryType | null>(null);

    const [originProducts] = createCachedResource(getOriginProductsQuery);
    const [productCategoryData] = createCachedResource(getProductCategoryQuery, { slug: categorySlug });

    // We have to do this in a roundabout way because of some kind of limitation in createCachedResource
    // this we can't diretly pass as the reative data to createResource, so we need to make a signal first
    // so that data IS reactive. This means we only fetch on the client of course, but this is fine.
    // This is 100 because of our home-made way of handling SSR but it's no biggie I guess.
    createEffect(() => {
        if (productCategoryData()?.productCategory) {
            setCategoryData(productCategoryData()?.productCategory);
        }
    });

    const [regNos] = createResource(categoryData, async (args) => {
        const products = args?.products || [];
        const slugs = [...products].map((product: { slug: string }) => product.slug);
        if (!slugs.length) {
            return [];
        }

        const query = (await graphQlClient(multiSearchQuery, { productSlugs: slugs })) as { multiSearch: MultiSearchResultType[] };
        return Array.isArray(query.multiSearch) ? query.multiSearch : [];
    });

    return (
        <Screen>
            <ProductCategory productCategoryData={productCategoryData} regNos={regNos} originProducts={originProducts} />
        </Screen>
    );
};
